import React, { useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area, MediaSize, Point } from 'react-easy-crop'

import { Button, Checkbox, ModalBox } from '@fourthwall/components'
import { base64ToFile } from '@fourthwall/utils/dist/file'

import * as S from './GalleryImageUploader.styled'
import type { Based64ImageWithType, UploadBlob } from './GalleryImageUploader.types'
import { getCroppedImage } from './GalleryImageUploader.utils'

const DEFAULT_ASPECT = 3 / 4

const getConfirmLabel = (index: number, totalFiles: number, filesToUpload: number) => {
  // NOTE: we take into account skipped photos, that's why we add 1 to the filesToUpload count
  if (totalFiles > 1 && index === totalFiles - 1) {
    return `Add ${filesToUpload + 1} photos`
  }

  return 'Add photo'
}

export const GalleryImageUploader = ({
  images,
  uploadToAllVariantsVisible,
  onClose,
  uploadBlobs,
}: {
  images: Array<Based64ImageWithType>
  uploadToAllVariantsVisible: boolean
  onClose: () => void
  uploadBlobs: (images: UploadBlob[]) => Promise<void>
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const processedImagesRef = useRef<UploadBlob[]>([])

  const [imageSize, setImageSize] = useState<MediaSize>()
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState<number>(1)
  const [aspect, setAspect] = useState<number | undefined>(DEFAULT_ASPECT)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>()
  const [uploadToAllSelected, setUploadToAllSelected] = useState<boolean>(false)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const imageAspect = imageSize ? imageSize.naturalWidth / imageSize.naturalHeight : undefined

  const handleZoomChange = (zoomValue: number) => setZoom(zoomValue)
  const handleCropComplete = (percentages: Area, pixels: Area) => setCroppedAreaPixels(pixels)
  const handleMediaLoad = (mediaSize: MediaSize) => {
    setAspect(DEFAULT_ASPECT)
    setImageSize(mediaSize)
  }

  const handleRatioCheckboxChange = (value: boolean) => {
    setAspect(value ? DEFAULT_ASPECT : imageAspect)
  }

  const handleUploadToAllChange = (value: boolean) => setUploadToAllSelected(value)

  const handleConfirm = async () => {
    const currentImage = images[activeImageIndex]
    const isOriginalImage = zoom === 1 && aspect === imageAspect

    const processedImage =
      croppedAreaPixels && !isOriginalImage
        ? await getCroppedImage(currentImage[0], croppedAreaPixels, currentImage[1])
        : await base64ToFile(currentImage[0], 'image', currentImage[1])

    const readyToUploadImage: UploadBlob = {
      blob: processedImage as Blob,
      uploadToAllVariants: uploadToAllSelected,
    }

    processedImagesRef.current = [...processedImagesRef.current, readyToUploadImage]

    if (activeImageIndex === images.length - 1) {
      onClose()
      await uploadBlobs([...processedImagesRef.current])
    } else {
      setActiveImageIndex((prevState) => prevState + 1)
    }
  }

  const handleSkip = async () => {
    if (activeImageIndex === images.length - 1) {
      if (processedImagesRef.current.length > 0) {
        await uploadBlobs([...processedImagesRef.current])
      }
      onClose()
    } else {
      setActiveImageIndex((prevState) => prevState + 1)
    }
  }

  const currentImage = images[activeImageIndex] || ['', 'image/jpeg']

  return (
    <ModalBox
      title={
        images.length > 1
          ? `Upload new photo (${activeImageIndex + 1}/${images.length})`
          : 'Upload new photo'
      }
      size="large"
      onClose={onClose}
    >
      <S.CropperContainer>
        <Cropper
          image={currentImage[0]}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          showGrid={false}
          onCropChange={setCrop}
          onMediaLoaded={handleMediaLoad}
          style={{
            cropAreaStyle: {
              color: 'rgba(255,255,255,0.75)',
            },
          }}
          onCropComplete={handleCropComplete}
          onZoomChange={handleZoomChange}
        />
      </S.CropperContainer>
      <S.Footer>
        <S.CheckboxContainer>
          {imageSize && imageAspect !== DEFAULT_ASPECT && (
            <Checkbox
              value={aspect === DEFAULT_ASPECT}
              label="Crop to 3:4 aspect ratio (recommended)"
              onValueChange={handleRatioCheckboxChange}
            />
          )}
          {uploadToAllVariantsVisible && (
            <Checkbox
              value={uploadToAllSelected}
              label="Upload to all variants"
              onValueChange={handleUploadToAllChange}
            />
          )}
        </S.CheckboxContainer>

        <S.ButtonContainer>
          {images.length > 1 && <Button label="Skip" appearance="secondary" onClick={handleSkip} />}
          <Button
            label={getConfirmLabel(
              activeImageIndex,
              images.length,
              processedImagesRef.current.length,
            )}
            appearance="primary"
            onClick={handleConfirm}
          />
        </S.ButtonContainer>
      </S.Footer>
    </ModalBox>
  )
}
